import {
  createPOSDomainAdapter,
} from "../../engines/POSDomainAdapter";

import {
  RETAIL_POS_DOMAIN_ID,
  validateRetailPOSConfiguration,
} from "./retailPOSConfiguration";


/**
 * Retail POS Domain Adapter.
 *
 * This adapter is an orchestration boundary only.
 *
 * It does not own:
 *
 * - Catalog state;
 * - Inventory state;
 * - Cart state;
 * - Customer state;
 * - Loyalty state;
 * - Pricing;
 * - Discount calculation;
 * - Tax calculation;
 * - Payment execution;
 * - SalesReceipt creation;
 * - SalesLine creation;
 * - Returns;
 * - Refunds;
 * - CashSession state;
 * - persistence.
 *
 * All dynamic business capabilities remain owned by
 * their existing engines and are injected when needed.
 */
export function createRetailPOSDomainAdapter({
  catalogProvider = null,
  sessionBridge = null,
  workspaceComponent = null,
  dispose = null,
} = {}) {
  assertOptionalFunction(
    catalogProvider,
    "catalogProvider"
  );

  assertOptionalSessionBridge(
    sessionBridge
  );

  assertOptionalComponent(
    workspaceComponent
  );

  assertOptionalFunction(
    dispose,
    "dispose"
  );


  return createPOSDomainAdapter({
    supportsActivity({
      companyUuid,
      activityUuid,
      activePublication,
      runtimeConfiguration,
    } = {}) {
      if (
        !isNonEmptyString(
          companyUuid
        )
        || !isNonEmptyString(
          activityUuid
        )
      ) {
        return false;
      }

      const configuration =
        runtimeConfiguration
        ?? null;

      if (
        configuration !== null
        && configuration
          !== undefined
      ) {
        const validation =
          validateRetailPOSConfiguration(
            configuration
          );

        if (!validation.valid) {
          return false;
        }

        return (
          configuration.domainId
            === RETAIL_POS_DOMAIN_ID
          && configuration.companyUuid
            === companyUuid
          && configuration.activityUuid
            === activityUuid
        );
      }

      return (
        activePublication?.domainId
          === RETAIL_POS_DOMAIN_ID
        && activePublication
          ?.companyUuid
          === companyUuid
        && activePublication
          ?.activityUuid
          === activityUuid
      );
    },


    getDomainId() {
      return RETAIL_POS_DOMAIN_ID;
    },


    validateConfiguration(
      configuration
    ) {
      const baseValidation =
        validateRetailPOSConfiguration(
          configuration
        );

      if (!baseValidation.valid) {
        return baseValidation;
      }

      const issues = [];

      if (
        configuration.domainId
          !== RETAIL_POS_DOMAIN_ID
      ) {
        issues.push(
          createIssue(
            "domainId",
            "INVALID_RETAIL_DOMAIN",
            'Retail POS requires domainId "retail".'
          )
        );
      }

      return Object.freeze({
        valid:
          issues.length === 0,

        issues:
          Object.freeze(
            issues
          ),
      });
    },


    async createSession({
      companyUuid,
      activityUuid,
      terminalUuid,
      deviceUuid,
      userUuid,
    } = {}) {
      const scope =
        normalizeActivityScope({
          companyUuid,
          activityUuid,
        });

      if (sessionBridge === null) {
        return Object.freeze({
          companyUuid:
            scope.companyUuid,

          activityUuid:
            scope.activityUuid,

          cashSession:
            null,
        });
      }

      const cashSession =
        await sessionBridge
          .resolveCurrentSession({
            companyUuid:
              scope.companyUuid,

            activityUuid:
              scope.activityUuid,

            ...optionalStringField(
              "terminalUuid",
              terminalUuid
            ),

            ...optionalStringField(
              "deviceUuid",
              deviceUuid
            ),

            ...optionalStringField(
              "userUuid",
              userUuid
            ),
          });

      return Object.freeze({
        companyUuid:
          scope.companyUuid,

        activityUuid:
          scope.activityUuid,

        cashSession,
      });
    },


    async getCatalog({
      companyUuid,
      activityUuid,
      runtimeConfiguration,
    } = {}) {
      const scope =
        normalizeActivityScope({
          companyUuid,
          activityUuid,
        });

      assertRuntimeScope(
        runtimeConfiguration,
        scope
      );

      if (catalogProvider === null) {
        return Object.freeze([]);
      }

      const catalog =
        await Promise.resolve(
          catalogProvider({
            companyUuid:
              scope.companyUuid,

            activityUuid:
              scope.activityUuid,

            configuration:
              runtimeConfiguration,
          })
        );

      if (!Array.isArray(catalog)) {
        throw new TypeError(
          "Retail catalogProvider must return an array."
        );
      }

      return Object.freeze([
        ...catalog,
      ]);
    },


    getNavigation({
      runtimeConfiguration,
    } = {}) {
      assertRetailRuntimeConfiguration(
        runtimeConfiguration
      );

      return cloneAndFreezeArray(
        runtimeConfiguration
          .workspaceLayout
          .navigation
      );
    },


    getAvailableActions({
      runtimeConfiguration,
    } = {}) {
      assertRetailRuntimeConfiguration(
        runtimeConfiguration
      );

      return Object.freeze([
        ...cloneArray(
          runtimeConfiguration
            .customerActions
        ),

        ...cloneArray(
          runtimeConfiguration
            .loyaltyActions
        ),

        ...cloneArray(
          runtimeConfiguration
            .paymentMethods
        ),

        ...cloneArray(
          runtimeConfiguration
            .receiptActions
        ),
      ]);
    },


    getWorkspaceDescriptor({
      runtimeConfiguration,
    } = {}) {
      assertRetailRuntimeConfiguration(
        runtimeConfiguration
      );

      return deepFreeze({
        id:
          resolveWorkspaceId(
            runtimeConfiguration
          ),

        domainId:
          RETAIL_POS_DOMAIN_ID,

        companyUuid:
          runtimeConfiguration
            .companyUuid,

        activityUuid:
          runtimeConfiguration
            .activityUuid,

        publicationId:
          runtimeConfiguration
            .publicationId,

        layout:
          cloneValue(
            runtimeConfiguration
              .workspaceLayout
              .layout
          ),

        workspaces:
          cloneValue(
            runtimeConfiguration
              .workspaceLayout
              .workspaces
          ),

        themeReference:
          cloneValue(
            runtimeConfiguration
              .themeReference
          ),

        scannerBindings:
          cloneValue(
            runtimeConfiguration
              .scannerBindings
          ),

        terminalBindings:
          cloneValue(
            runtimeConfiguration
              .terminalBindings
          ),
      });
    },


    resolveComponent() {
      return workspaceComponent;
    },


    async dispose(context = {}) {
      if (dispose === null) {
        return undefined;
      }

      return Promise.resolve(
        dispose(context)
      );
    },
  });
}


export function isRetailPOSDomainAdapter(
  adapter
) {
  if (
    adapter === null
    || typeof adapter !== "object"
  ) {
    return false;
  }

  try {
    return (
      adapter.getDomainId()
        === RETAIL_POS_DOMAIN_ID
    );
  } catch {
    return false;
  }
}


function assertRuntimeScope(
  configuration,
  {
    companyUuid,
    activityUuid,
  }
) {
  assertRetailRuntimeConfiguration(
    configuration
  );

  if (
    configuration.companyUuid
      !== companyUuid
  ) {
    throw new TypeError(
      "RetailPOSConfiguration companyUuid does not match the requested company scope."
    );
  }

  if (
    configuration.activityUuid
      !== activityUuid
  ) {
    throw new TypeError(
      "RetailPOSConfiguration activityUuid does not match the requested activity scope."
    );
  }
}


function assertRetailRuntimeConfiguration(
  configuration
) {
  const validation =
    validateRetailPOSConfiguration(
      configuration
    );

  if (!validation.valid) {
    const details =
      validation.issues
        .map(
          (issue) =>
            `${issue.path}: ${issue.message}`
        )
        .join("; ");

    throw new TypeError(
      details
        ? `Invalid RetailPOSConfiguration: ${details}`
        : "Invalid RetailPOSConfiguration."
    );
  }
}


function normalizeActivityScope({
  companyUuid,
  activityUuid,
}) {
  return Object.freeze({
    companyUuid:
      normalizeRequiredString(
        companyUuid,
        "companyUuid"
      ),

    activityUuid:
      normalizeRequiredString(
        activityUuid,
        "activityUuid"
      ),
  });
}


function resolveWorkspaceId(
  configuration
) {
  const firstWorkspace =
    configuration
      .workspaceLayout
      .workspaces[0];

  if (
    firstWorkspace
    && typeof firstWorkspace
      === "object"
    && isNonEmptyString(
      firstWorkspace.id
    )
  ) {
    return firstWorkspace.id;
  }

  return "retail-pos";
}


function optionalStringField(
  fieldName,
  value
) {
  if (
    value === null
    || value === undefined
    || value === ""
  ) {
    return {};
  }

  return {
    [fieldName]:
      normalizeRequiredString(
        value,
        fieldName
      ),
  };
}


function normalizeRequiredString(
  value,
  fieldName
) {
  if (!isNonEmptyString(value)) {
    throw new TypeError(
      `${fieldName} must be a non-empty string.`
    );
  }

  return value.trim();
}


function isNonEmptyString(
  value
) {
  return (
    typeof value === "string"
    && value.trim() !== ""
  );
}


function assertOptionalFunction(
  value,
  fieldName
) {
  if (
    value !== null
    && typeof value
      !== "function"
  ) {
    throw new TypeError(
      `${fieldName} must be a function when provided.`
    );
  }
}


function assertOptionalSessionBridge(
  bridge
) {
  if (bridge === null) {
    return;
  }

  if (
    typeof bridge !== "object"
    || typeof bridge
      .resolveCurrentSession
      !== "function"
  ) {
    throw new TypeError(
      "sessionBridge must expose resolveCurrentSession()."
    );
  }
}


function assertOptionalComponent(
  component
) {
  if (component === null) {
    return;
  }

  if (
    typeof component !== "function"
    && typeof component
      !== "object"
  ) {
    throw new TypeError(
      "workspaceComponent must be a React-compatible component when provided."
    );
  }
}


function cloneAndFreezeArray(
  value
) {
  return deepFreeze(
    cloneArray(value)
  );
}


function cloneArray(
  value
) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(
    cloneValue
  );
}


function cloneValue(
  value
) {
  if (Array.isArray(value)) {
    return value.map(
      cloneValue
    );
  }

  if (
    value !== null
    && typeof value === "object"
  ) {
    return Object.fromEntries(
      Object.entries(value)
        .map(
          ([key, entry]) => [
            key,
            cloneValue(entry),
          ]
        )
    );
  }

  return value;
}


function deepFreeze(
  value
) {
  if (
    value === null
    || typeof value
      !== "object"
    || Object.isFrozen(value)
  ) {
    return value;
  }

  Object.freeze(value);

  Object.values(value)
    .forEach(
      deepFreeze
    );

  return value;
}


function createIssue(
  path,
  code,
  message
) {
  return Object.freeze({
    path,
    code,
    message,
  });
}
